import React, { FC, createContext, useContext, useState, useRef, useEffect, ReactNode } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
// import { push } from 'connected-react-router'
import { PageHeader, Space, Table, Form, Input, Select, Button, Typography, Popover, ConfigProvider } from 'antd'
import { FormOutlined, ClockCircleOutlined } from '@ant-design/icons'
import type { InputRef, PaginationProps } from 'antd'
import type { FormInstance } from 'antd/es/form'
import zhCN from 'antd/es/locale/zh_CN'
import type { IDataType, IKeyRoadMapResData } from '@/store/models/tableEdit.model'
import { useTranslation } from 'react-i18next'
// import { dataSource1 } from '@/helpers/tableData'
import type { IAppState } from '@/store/reducers'
import { getKeyRoadMap, getWorkgroup, setKeyRoadMapPagination } from '@/store/actions/tableEdit.action'
import LogListPop from '@/pages/table2Mana/components/logListPop'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import Nav from '@/components/nav'

interface EditableRowProps {
    index: number
}
interface EditableCellProps {
    title: ReactNode
    editable: boolean
    children: ReactNode
    dataIndex: string
    record: any
}
type EditableTableProps = Parameters<typeof Table>[0]
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const EditableContext = createContext<FormInstance<any> | null>(null)

const EditableRow: FC<EditableRowProps> = ({ ...props }) => {
    const [form] = Form.useForm()
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    )
}
const EditableCell: FC<EditableCellProps> = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false)
    const inputRef = useRef<InputRef>(null)
    const form = useContext(EditableContext)
  
    useEffect(() => {
        if (editing) {
            inputRef.current?.focus()
        }
    }, [editing])
  
    const toggleEdit = () => {
        if (record[dataIndex].includes('WG')) return
        setEditing(!editing)
        form?.setFieldsValue({ [dataIndex]: record[dataIndex] })
    }
  
    const save = async () => {
        try {
            toggleEdit()
        } catch (errInfo) {
            console.log('Save failed:', errInfo)
        }
    }
  
    let childNode = children
  
    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{ margin: 0 }}
                name={dataIndex}
                rules={[
                    {
                        required: true,
                        message: `${title} is required.`,
                    },
                ]}
            >
                <Input ref={inputRef} onPressEnter={save} onBlur={save} />
            </Form.Item>
        ) : (
            <div className="editable-cell-value-wrap" style={{ paddingRight: 24 }} onClick={toggleEdit}>
                {children}
            </div>
        )
    }
  
    return <td {...restProps}>{childNode}</td>
}

const TablePage1: FC = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()
    const { Text } = Typography
    const route = useSelector((state: IAppState) => state.router)
    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const keyRoadMap = useSelector((state: IAppState) => state.tableEditData.keyRoadMap)
    const paginationInfo = useSelector((state: IAppState) => state.tableEditData.keyRoadMapPagination)
    // 按 workgroup 查询
    const [filterCond, setFilterCond] = useState<number | null>(null)
    const [columns, setColumns] = useState<any[]>([])
    const [tableData, setTableData] = useState<any[]>([])
    const [viewLogId, setViewLogId] = useState<number | null>(null)
    const [tableKey, setTableKey] = useState(0)
    const { Option } = Select
    // pageSize 变更后，会重复触发 pageChange 事件
    const pageSizeChanged = useRef<boolean>(false)

    const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] = [
        {
            title: t('Key Info Index'),
            dataIndex: 'name',
            key: 'name',
            className: 'bg',
            width: 160,
            // render: (text: string) => {
            //     const textArr = text.split(' ')
            //     const cnText = textArr.shift()
            //     return <Space>
            //         <span>{cnText}<br/>{textArr.join(' ')}</span>
            //         {/* 编辑按钮 */}
            //         {/* {text !== '' && <FormOutlined onClick={() => {
            //             dispatch(setEditData(record as IDataType))
            //             dispatch(push('/table1Edit', record))
            //             history.push({ pathname: '/table1Edit' })
            //         }} />} */}
            //     </Space>
            // },
        },
        {
            title: t('Work Group/Roadmap'),
            dataIndex: 'workgroup',
            key: 'workgroup',
            editable: false,
            render: (text: string, record: any) => {
                if (text !== '') {
                    if (record.name === '') {
                        return <Space>
                            <div style={{ whiteSpace: 'nowrap' }}>{text}</div>
                            {authInfo && Reflect.has(authInfo, 'editRoadMap') &&
                            <Text className='txt_blue hand'
                                onClick={() => {
                                    console.log('record: ', record)
                                    // dispatch(push('/keyRoadMapEdit', record.rowData))
                                    history.push('/keyRoadMapEdit?roadMapId=' + record.id)
                                }} title={t('edit')}
                            ><FormOutlined style={{ fontSize: 16 }} /></Text>}
                            {authInfo && Reflect.has(authInfo, 'viewLogGroupRoadMap') &&
                            <Text className='hand' title={t('viewLog')}
                                onClick={() => {
                                    setViewLogId(record.id)
                                }}
                            ><ClockCircleOutlined style={{ fontSize: 16 }} /></Text>}
                        </Space>
                    } else {
                        return <Popover 
                            title=""
                            content={
                                <b>{text}</b>
                            }
                        >
                            <div className='table_ellipsis'>{text}</div>
                        </Popover>
                    }
                }
            },
        }
    ]
    
    const getTableData = () => {
        const result: any[] = []
        keyRoadMap.forEach((roadMap, index) => {
            const arr: any[] = []
            const findWorkgroup = workgroupList.find(workgroupItem => workgroupItem.id === roadMap.workGroupId)
            arr.push({
                name: '',
                workgroup: findWorkgroup?.groupName,
                id: roadMap.id,
                rowData: roadMap
            })
            arr.push({
                name: t('Tech Name'),
                workgroup: roadMap.techName,
                id: `techName_${roadMap.id}`,
                className: `bg_${index % 5 + 1}`
            })
            arr.push({
                name: t('PTN'),
                workgroup: roadMap.techPtn,
                id: `techPtn_${roadMap.id}`,
                className: `bg_${index % 5 + 1}`
            })
            arr.push({
                name: t('Project Info'),
                workgroup: roadMap.projectInfo,
                id: `projectInfo_${roadMap.id}`,
                className: `bg_${index % 5 + 1}`
            })
            arr.push({
                name: t('Released'),
                workgroup: roadMap.releaseStatus,
                id: `releaseStatus_${roadMap.id}`,
                className: `bg_${index % 5 + 1}`
            })
            roadMap.phases?.forEach(phase => {
                arr[0][phase.year as number] = ''
                arr[1][phase.year as number] = phase.techName
                arr[2][phase.year as number] = phase.techPtn
                arr[3][phase.year as number] = phase.projectInfo
                arr[4][phase.year as number] = phase.releaseStatus
            })
            result.push.apply(result, [...arr])
        })
        return result
    }
    const getColumns = () => {
        const _defaultColumns = [...defaultColumns]
        const yearMinMaxObj = keyRoadMap.reduce((result, item) => {
            let minYear = result.minYear
            let maxYear = result.maxYear
            if (item.phases) {
                minYear = (item.phases as IKeyRoadMapResData[]).reduce((min: number, phase: any) => {
                    return min < phase.year ? min : phase.year as number
                }, result.minYear)
                maxYear = (item.phases as IKeyRoadMapResData[]).reduce((max: number, phase: any) => {
                    return max > phase.year ? max : phase.year as number
                }, result.maxYear)
            }
            return {
                minYear, maxYear
            }
        }, { minYear: 9999, maxYear: 0 })
        for (let year = yearMinMaxObj.minYear; year <= yearMinMaxObj.maxYear; year++) {
            _defaultColumns.push({
                title: year,
                dataIndex: String(year),
                key: String(year),
                width: 150,
                render: (text: string) => {
                    return <Popover 
                        title=""
                        content={
                            <b>{text}</b>
                        }
                    >
                        <div className='table_ellipsis'>{text}</div>
                    </Popover>
                }
            })
        }
        return _defaultColumns.map(col => {
            if (!col.editable) {
                return col
            }
            return {
                ...col,
                onCell: (record: IDataType) => ({
                    record,
                    editable: col.editable,
                    dataIndex: col.dataIndex,
                    title: col.title,
                }),
            }
        })
    }

    // 分页
    const onPaginationChange: PaginationProps['onChange'] = (pageNumber: number): void => {
        if (pageSizeChanged.current) return
        dispatch(getKeyRoadMap({
            workGroupId: filterCond || '',
            pageNo: pageNumber,
            pageSize: paginationInfo.pageSize
        }))
        dispatch(setKeyRoadMapPagination({
            pageIndex: pageNumber
        }))
    }
    const onPageSizeChange = (current: number, size: number) => {
        // 避免重复触发 pageChange 事件
        pageSizeChanged.current = true
        dispatch(getKeyRoadMap({
            workGroupId: filterCond || '',
            pageNo: 1,
            pageSize: size
        }))
        dispatch(setKeyRoadMapPagination({
            pageIndex: 1,
            pageSize: size
        }))
        setTimeout(() => {
            pageSizeChanged.current = false
        }, 100)
    }

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    }
    const routes = [
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/keyRoadMap',
            breadcrumbName: t('technology roadmap') + (i18n.language !== 'zh' ? ' ' : '') + t('list'),
        },
    ]
    useEffect(() => {
        // 获取 workgroupList
        if (workgroupList.length === 0) {
            dispatch(getWorkgroup())
        }
        // 获取 keyRoadMapList
        dispatch(getKeyRoadMap({
            workGroupId: '',
            pageNo: paginationInfo.pageIndex,
            pageSize: paginationInfo.pageSize
        }))
    }, [])
    useEffect(() => {
        setColumns(getColumns())
        if (workgroupList.length && keyRoadMap.length) {
            setTableData(getTableData())
        } else {
            setTableData([])
        }
    }, [workgroupList, keyRoadMap])
    // 过滤列
    useEffect(() => {
        // console.log('route: ', route)
        // if (filterCond.length === 0) {
        //     setTableData([...dataSource])
        // } else {
        //     const findIndex = dataSource.findIndex(item => item.workgroup.includes(filterCond))
        //     setTableData(dataSource.slice(findIndex, findIndex + 21))
        // }
    }, [filterCond, route])
    useEffect(() => {
        setColumns(getColumns())
        if (workgroupList.length && keyRoadMap.length) {
            setTableData(getTableData())
            setTableKey(tableKey + 1)
        }
    }, [i18n.language])
    return (
        <>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                route={route}
                dispatch={dispatch}
            />
            <Space direction="vertical" size={0} style={{ display: 'flex' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PageHeader className='chart_title'
                        onBack={() => null}
                        title={t('technology roadmap') + (i18n.language !== 'zh' ? ' ' : '') + t('list')}
                    />
                    <Space>
                        <Select
                            allowClear style={{ minWidth: 200 }}
                            placeholder={t('Select Cond')}
                            onChange={(value: number) => {
                                if (typeof value !== 'undefined') {
                                    setFilterCond(value)
                                    dispatch(getKeyRoadMap({
                                        workGroupId: value,
                                        pageNo: paginationInfo.pageIndex,
                                        pageSize: paginationInfo.pageSize
                                    }))
                                } else {
                                    setFilterCond(null)
                                    dispatch(getKeyRoadMap({
                                        workGroupId: '',
                                        pageNo: paginationInfo.pageIndex,
                                        pageSize: paginationInfo.pageSize
                                    }))
                                }
                            }}
                        >
                            {/* <Option value='' key={-1}>请选择条件</Option> */}
                            {workgroupList.map(item => (
                                <Option value={item.id} key={item.id}>{item.groupName}</Option>
                            ))}
                        </Select>
                        {/* table1Add 已弃用，功能已合并到 keyRoadMapAdd */}
                        {/* <Button type='primary' onClick={() => {
                            history.push('/table1Add')
                        }}>新增</Button> */}
                        {authInfo && Reflect.has(authInfo, 'manageRoadMap') &&
                        <Button type='primary' onClick={() => {
                            // dispatch(push('/keyRoadMapAdd'))
                            history.push('/keyRoadMapAdd')
                        }}>{t('manage') + (i18n.language !== 'zh' ? ' ' : '') + t('technology roadmap')}</Button>}
                    </Space>
                </div>
                <ConfigProvider locale={i18n.language === 'zh' ? zhCN : undefined}>
                    <Table className='index_ls' bordered
                        rowKey='id'
                        key={tableKey}
                        dataSource={tableData}
                        components={components}
                        columns={columns as ColumnTypes}
                        rowClassName={(record: any) => {
                            return `${record.className || ''} editable-row`
                        }}
                        pagination={{
                            current: paginationInfo.pageIndex,
                            pageSize: paginationInfo.pageSize,
                            total: paginationInfo.total,
                            showQuickJumper: true,
                            onChange: onPaginationChange,
                            showSizeChanger: true,
                            onShowSizeChange: onPageSizeChange,
                            showTotal: (total) => 
                                `${t('total')} ${total} ${t('items')}`
                        }}
                    />
                </ConfigProvider>
            </Space>
            <LogListPop recordId={viewLogId} tableParam='key_tech_road_map' setViewLogId={setViewLogId} />
        </>
    )
}

export default TablePage1
